// @ts-strict-ignore
import { InstitutionSettingsResponseType } from 'fetchers/responses/instituion-settings.response';

import { SymptomTicketUrgency } from 'constants/urgency.consts';

import { DxCode } from 'models/Conditions/SpecificCauseCondition';

import { DepressionScreeningAnswer } from 'models/DepressionScreening';
import {
  OralReportResponse,
  OralReportVersion,
  ReportQuestionAnswer
} from 'models/OralReport/OralReport.shared';
import {
  OralAnswerValueV1,
  OralReportV1,
  OralQuestionTypeV1
} from 'models/OralReport/OralReportV1';
import {
  OralAnswerValueV2,
  OralReportV2,
  OralQuestionTypeV2
} from 'models/OralReport/OralReportV2';
import {
  OralAnswerValueV3,
  OralQuestionTypeV3,
  OralReportV3
} from 'models/OralReport/OralReportV3';
import { IPatientContact } from 'models/Patient';
import { PatientEpisodeEndReason } from 'models/PatientEpisodeEndReason';
import PatientLocation from 'models/PatientLocation';
import QuestionnaireAnswer, {
  IReportAnswer,
  IReportedInputQuestion,
  IReportedYesNoQuestion,
  ReportSource,
  ReportType
} from 'models/QuestionnaireAnswer';
import SeverityReportedCause, {
  isNumericReportedCause,
  isSeverityReportedCause,
  NumericReportedCause,
  ReportCauseChange
} from 'models/ReportedCause';
import InstitutionSettings from 'models/Settings';

export interface IReportFromServer {
  id: number;
  type: ReportType;
  createdAt: string;
  patientReportIndex: number;
  answer: {
    causes?: ReportedCauseFromServer[];
    distressLevel?: number;
    callbackRequest?: ReportQuestionAnswer<boolean>;
    oral?: OralReportResponse;
    inputQuestions?: any[];
    yesNoQuestions?: any[];
    answers?: DepressionScreeningAnswer[];
    version?: 2;
  };
  ticketId: number;
  source: ReportSource;
  urgency: SymptomTicketUrgency;
}

export interface BaseReportedCauseFromServer {
  causeId: number;
  change?: ReportCauseChange;
  dxCodes?: DxCode[];
}

export type ReportedCauseFromServer =
  | SeverityReportedCauseFromServer
  | NumericReportedCauseFromServer;

export interface SeverityReportedCauseFromServer extends BaseReportedCauseFromServer {
  severity: number;
}

export interface NumericReportedCauseFromServer extends BaseReportedCauseFromServer {
  value: number | 'iDontKnow';
}

export default class ParseServerResponseService {
  static parsePatientContact = (unparsedContact: any): IPatientContact => {
    if (!unparsedContact) {
      return;
    }
    return {
      id: unparsedContact.id,
      relationship: unparsedContact.relationship,
      name: unparsedContact.name,
      email: unparsedContact.email,
      language: unparsedContact.language,
      phoneNumber: unparsedContact.phone_number || unparsedContact.phoneNumber,
      phoneType: unparsedContact.phone_type || unparsedContact.phoneType,
      phoneExtension: unparsedContact.phone_extension || unparsedContact.phoneExtension,
      deletedAt: unparsedContact.deleted_at || unparsedContact.deletedAt,
      smsOptedOutAt: unparsedContact.sms_opted_out_at || unparsedContact.smsOptedOutAt
    };
  };

  static parseLocation = (unparsedLocation: any) => {
    return new PatientLocation(unparsedLocation);
  };

  static parseReport = (unparsedReport: IReportFromServer) => {
    const questionnaire = new QuestionnaireAnswer();
    questionnaire.id = unparsedReport.id;
    questionnaire.type = unparsedReport.type;
    questionnaire.createdAt = unparsedReport.createdAt;
    questionnaire.answer = ParseServerResponseService.parseReportAnswer(unparsedReport);
    questionnaire.patientReportIndex = unparsedReport.patientReportIndex;
    questionnaire.ticketId = unparsedReport.ticketId;
    questionnaire.source = unparsedReport.source;
    questionnaire.urgency = unparsedReport.urgency;
    return questionnaire;
  };

  static parseAnswerOral(answer: any) {
    switch (answer.version) {
      case OralReportVersion.V2: {
        return new OralReportV2(
          answer.oral as OralReportResponse<OralAnswerValueV2, OralQuestionTypeV2>
        );
      }
      case OralReportVersion.V3: {
        return new OralReportV3(
          answer.oral as OralReportResponse<OralAnswerValueV3, OralQuestionTypeV3>
        );
      }
      default: {
        return new OralReportV1(
          answer.oral as OralReportResponse<OralAnswerValueV1, OralQuestionTypeV1>
        );
      }
    }
  }

  static parseReportAnswer = (unparsedReport: IReportFromServer) => {
    const answer: IReportAnswer = {};
    switch (unparsedReport.type) {
      case ReportType.Oral:
        answer.distressLevel = unparsedReport.answer.distressLevel;
        answer.causes = ParseServerResponseService.parseReportCauses(unparsedReport);

        answer.oral = ParseServerResponseService.parseAnswerOral(unparsedReport.answer);

        answer.callbackRequest = new ReportQuestionAnswer<boolean>(
          unparsedReport.answer.callbackRequest
        );
        answer.version = unparsedReport.answer.version;
        break;

      case ReportType.Distress:
      case ReportType.DrugSpecific:
        answer.distressLevel = unparsedReport.answer.distressLevel;
        answer.causes = ParseServerResponseService.parseReportCauses(unparsedReport);
        answer.callbackRequest = new ReportQuestionAnswer<boolean>(
          unparsedReport.answer.callbackRequest
        );
        break;

      case ReportType.COVID:
        answer.causes = ParseServerResponseService.parseReportCauses(unparsedReport);
        answer.inputQuestions = unparsedReport.answer.inputQuestions
          ? unparsedReport.answer.inputQuestions.map((unparsedQuestion: any) => {
              const reportedQuestion: IReportedInputQuestion = {
                id: unparsedQuestion.id,
                questionText: unparsedQuestion.questionText,
                inputType: unparsedQuestion.inputType,
                answer: unparsedQuestion.answer
              };
              return reportedQuestion;
            })
          : [];
        answer.yesNoQuestions = unparsedReport.answer.yesNoQuestions.map(
          (unparsedQuestion: any) => {
            const reportedQuestion: IReportedYesNoQuestion = {
              id: unparsedQuestion.id,
              questionText: unparsedQuestion.questionText,
              value: unparsedQuestion.value
            };
            return reportedQuestion;
          }
        );
        break;

      case ReportType.DepressionScreening:
        if (unparsedReport.answer.answers) {
          // Store raw depression screening answers
          answer.answers = unparsedReport.answer.answers;
        }
        break;
    }
    return answer;
  };

  private static parseReportCauses(unparsedReport: IReportFromServer) {
    return unparsedReport.answer.causes.map((unparsedCause) => {
      const { causeId } = unparsedCause;

      let reportedCause: SeverityReportedCause | NumericReportedCause;

      if (isSeverityReportedCause(unparsedCause)) {
        reportedCause = new SeverityReportedCause(causeId, unparsedCause.severity);
      } else if (isNumericReportedCause(unparsedCause)) {
        reportedCause = new NumericReportedCause(causeId, unparsedCause.value);
      }

      reportedCause.change = unparsedCause.change;
      return reportedCause;
    });
  }

  static parseInstitutionSettings(unparsedInstitutionSettings: InstitutionSettingsResponseType) {
    const { features, ...settings } = unparsedInstitutionSettings;
    return {
      settings: new InstitutionSettings(settings),
      features: features
    };
  }

  static parsePatientEpisodeEndReason(unparsedReasons: any[]): PatientEpisodeEndReason[] {
    return unparsedReasons.map((unparsedReason) => new PatientEpisodeEndReason(unparsedReason));
  }
}
